import React, { useState, useEffect } from 'react';
import { getGuardiaContacts, createGuardiaContact, updateGuardiaContact, deleteGuardiaContact, getDevOpsMembers } from './api';
import './ContactosGuardia.css';

const EMPTY_CONTACT = { name: '', email: '', phone: '', extension: '', category: 'equipo' };

const FIELD_NAMES = {
  name: 'nombre',
  email: 'email',
  phone: 'teléfono (formato internacional, p. ej. +34611740761)',
  extension: 'extensión (solo dígitos)',
  category: 'tipo'
};

const getErrorMessage = (error) => {
  const data = error.response?.data;
  if (data?.errors) {
    const fields = [...new Set(data.errors.map(e => FIELD_NAMES[e.path] || e.path))];
    return `Revisa los campos: ${fields.join(', ')}`;
  }
  return data?.error || 'Error al guardar el contacto';
};

function ContactForm({ initial, members, onSubmit, onDelete, submitLabel }) {
  const [values, setValues] = useState({ ...EMPTY_CONTACT, ...initial, extension: initial.extension || '', email: initial.email || '' });
  const [busy, setBusy] = useState(false);

  const isTeam = values.category === 'equipo';
  const hasChanges = ['name', 'email', 'phone', 'extension', 'category']
    .some(key => (values[key] || '') !== (initial[key] || ''));

  // Incluir el email actual aunque ya no esté en la lista de miembros DevOps
  const memberOptions = members.some(m => m.email === values.email) || !values.email
    ? members
    : [...members, { email: values.email, name: values.email }];

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const handleMemberChange = (email) => {
    const member = members.find(m => m.email === email);
    setValues(prev => ({ ...prev, email, name: prev.name || member?.name || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit(values);
    setBusy(false);
    if (ok && !initial.id) {
      setValues(EMPTY_CONTACT);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar el contacto "${initial.name}"?`)) return;
    setBusy(true);
    await onDelete(initial.id);
    setBusy(false);
  };

  return (
    <form className="config-item guardia-contact-form" onSubmit={handleSubmit}>
      <div className="guardia-contact-fields">
        <label>
          <span className="config-label">Tipo</span>
          <select
            value={values.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="config-input"
          >
            <option value="equipo">Equipo de guardia</option>
            <option value="especial">Contacto especial</option>
          </select>
        </label>

        {isTeam && (
          <label>
            <span className="config-label">Miembro</span>
            <select
              value={values.email}
              onChange={(e) => handleMemberChange(e.target.value)}
              className="config-input"
              required
            >
              <option value="">Selecciona un miembro</option>
              {memberOptions.map(member => (
                <option key={member.email} value={member.email}>{member.name}</option>
              ))}
            </select>
          </label>
        )}

        <label>
          <span className="config-label">Nombre</span>
          <input
            type="text"
            value={values.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="config-input"
            required
          />
        </label>

        <label>
          <span className="config-label">Teléfono</span>
          <input
            type="tel"
            value={values.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="config-input"
            placeholder="+34611740761"
            required
          />
        </label>

        {isTeam && (
          <label>
            <span className="config-label">Extensión Zadarma</span>
            <input
              type="text"
              inputMode="numeric"
              value={values.extension}
              onChange={(e) => handleChange('extension', e.target.value)}
              className="config-input"
              placeholder="121"
            />
          </label>
        )}
      </div>

      <div className="guardia-contact-actions">
        {(hasChanges || !initial.id) && (
          <button type="submit" disabled={busy} className="btn-save">
            {busy ? 'Guardando...' : submitLabel}
          </button>
        )}
        {initial.id && (
          <button type="button" onClick={handleDelete} disabled={busy} className="btn-delete-contact">
            Eliminar
          </button>
        )}
      </div>
    </form>
  );
}

function ContactosGuardia() {
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contactsResponse, membersResponse] = await Promise.all([getGuardiaContacts(), getDevOpsMembers()]);
      setContacts(contactsResponse.data);
      setMembers(membersResponse.data);
    } catch (error) {
      console.error('Error loading guardia contacts:', error);
      setError('Error al cargar los contactos de guardia');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setError(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSave = async (values) => {
    try {
      if (values.id) {
        const response = await updateGuardiaContact(values.id, values);
        setContacts(prev => prev.map(c => (c.id === values.id ? response.data : c)));
        showSuccess(`Contacto "${response.data.name}" actualizado`);
      } else {
        const response = await createGuardiaContact(values);
        setContacts(prev => [...prev, response.data]);
        showSuccess(`Contacto "${response.data.name}" añadido`);
      }
      return true;
    } catch (error) {
      console.error('Error saving guardia contact:', error);
      setSuccess(null);
      setError(getErrorMessage(error));
      return false;
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteGuardiaContact(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      showSuccess('Contacto eliminado');
    } catch (error) {
      console.error('Error deleting guardia contact:', error);
      setError(getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Cargando contactos...</p>
      </div>
    );
  }

  const renderGroup = (category, title) => (
    <>
      <h3 className="guardia-contacts-group">{title}</h3>
      {contacts.filter(c => c.category === category).map(contact => (
        // key con los valores para reiniciar el formulario cuando se guarda
        <ContactForm
          key={`${contact.id}-${contact.name}-${contact.email}-${contact.phone}-${contact.extension}-${contact.category}`}
          initial={contact}
          members={members}
          onSubmit={handleSave}
          onDelete={handleDelete}
          submitLabel="Guardar"
        />
      ))}
    </>
  );

  return (
    <>
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✅</span>
          {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="config-section-content">
        <p className="config-hint guardia-contacts-info">
          El lunes, la extensión de guardia (114) se desvía automáticamente a la <strong>Extensión Zadarma</strong> del N1.
          Esa extensión debe tener configurado en el panel de Zadarma un desvío "Siempre" a su móvil, con el país correcto.
        </p>

        {renderGroup('equipo', 'Equipo de guardia')}
        {renderGroup('especial', 'Contactos especiales')}

        <h3 className="guardia-contacts-group">Añadir contacto</h3>
        <ContactForm
          initial={EMPTY_CONTACT}
          members={members}
          onSubmit={handleSave}
          submitLabel="Añadir"
        />
      </div>
    </>
  );
}

export default ContactosGuardia;
